// @flow
import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, Button } from 'grommet';
import { FieldWrapper } from 'speed-js-react-web';
import { connectAPI, withRouter } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { ApiFormWithModel } from 'esm-web-pro/containers/forms/FormWithModel';
import BaseSelect from 'esm-web-pro/components/forms/fields/BaseSelect';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';


type Props = {
    point: Object,
    pointId: number,
    onClose: () => void,
    onSave: Function,
    history: { push: () => void },
    match: Object,
    map: Object,
    getInfrastructureProviders: ?(mapIdentity: string) => { infrastructureProviders: [] },
    infrastructureProviders: Array<Object>,
    loadFeatures: Function,
    focus?: Function,
};

const TransformPoint = (props: Props) => {
    const {
        point, onClose, onSave, map, getInfrastructureProviders, loadFeatures, pointId, focus,
    } = props;

    const [val, setValue] = useState(null);

    const [provider, setProvider] = useState(null);

    const [contentList, setContentList] = useState(null);

    const handleSetProvider = (uuid, providers) => {
        const prov: Object = providers.find((p) => p.uuid === uuid);
        setProvider(prov);
        const list = Object.values(prov.allowed_objects[0].content_types)
            .filter((p) => point.contain_points.find((c) => p.model === c))
            .reduce((objects, o) => {
                objects[o.model] = o.name;
                return objects;
            }, {});
        setContentList(list);
        setValue(null);
    };

    const handleProviderChange = (providerUUID: string) => {
        if (!providerUUID) return;
        const { infrastructureProviders } = props;
        handleSetProvider(providerUUID, infrastructureProviders);
    };

    useEffect(() => {
        if (getInfrastructureProviders) {
            getInfrastructureProviders(map.identity)
                .then((response) => response.results.forEach((prov) => {
                    if (prov.entity.name === process.env.REACT_APP_DEFAULT_PROVIDER) {
                        handleSetProvider(prov.uuid, response.results);
                    }
                }));
        }
        return () => {
            if (focus) {
                // Remove all focused point so that they can be removed from the map
                // because after the transform another point will be created and added to the map
                focus();
            }
        };
    }, []);

    const initial = {
        infrastructure_provider: provider ? provider.id : null,
        gis_point: pointId,
        coordinates: point.coordinates,
        map: map.uuid,
        alias: point.alias,
    };

    const renderProviderSelect = () => {
        const { infrastructureProviders } = props;

        const providerChoices = infrastructureProviders.reduce((providers, p) => {
            providers[p.uuid] = p.entity.name;
            return providers;
        }, {});

        return (
            <FieldWrapper label={(
                <FormattedMessage
                    id="infrastructure-provider-select-title"
                    defaultMessage="Infrastructure provider"
                />
            )}
            >
                <BaseSelect
                    emptyLabel={(
                        <FormattedMessage
                            id="infrastructure-provider-select"
                            defaultMessage="Select infrastructure provider..."
                        />
                    )}
                    choices={providerChoices}
                    value={provider ? provider.uuid : null}
                    onChange={handleProviderChange}
                />
            </FieldWrapper>
        );
    };

    const handleChooseContent = (value: string) => {
        setValue(value);
    };

    const handleOnSave = (response) => {
        if (response.status === 200) {
            onSave();
            const path = props.match.url.split('/layer');
            props.history.push(
                `${path[0]}/layer/${val}/${response.identity}`
                    .replace('//', '/'),
            );
            loadFeatures(true);
        }
    };

    const renderContentSelect = () => (
        <FieldWrapper label={(
            <FormattedMessage
                id="content"
                defaultMessage="Content"
            />
        )}
        >
            <BaseSelect
                emptyLabel={(
                    <FormattedMessage
                        id="content-select"
                        defaultMessage="Select content"
                    />
                )}
                choices={contentList}
                value={val}
                onChange={handleChooseContent}
            />
        </FieldWrapper>
    );

    const handleSubmit = (response) => {
    };

    const handleSave = (response) => {
        if (response.ok !== false) {
            onSave();
            const path = props.match.url.split('/layer');
            props.history.push(
                `${path[0]}/layer/${val}/${response.identity}`
                    .replace('//', '/'),
            );
            loadFeatures(true);
        }
    };

    return (
        <Box height="100%">
            <Box
                direction="row"
                align="center"
                height="100%"
                overflow="auto"
                style={{ position: 'unset' }}
            >
                <Box alignSelf="stretch" flex>
                    <Box pad={{ horizontal: 'medium' }} flex={{ shrink: 0 }}>
                        {renderProviderSelect()}
                        {provider && renderContentSelect()}
                    </Box>
                    <Box alignSelf="stretch" flex justify="between" />
                    {val ? (
                        <ApiFormWithModel
                            objectType={val}
                            key={val}
                            resourceName={val}
                            resourceMethod="createSingle"
                            useResourceMethodForSchema={false}
                            resourceArguments={[]}
                            excludeFields={['container', 'coordinates', 'infrastructure_provider', 'stage', 'ref']}
                            initial={initial}
                            height="100%"
                            onClose={onClose}
                            onSubmit={handleSubmit}
                            onSave={handleSave}
                        />
                    ) : (
                        <ActionButtons onClose={onClose} onSave={false} />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

TransformPoint.defaultProps = {
    getInfrastructureProviders: undefined,
    infrastructureProviders: [],
    focus: undefined,
};

const apiCallback = (apiClient: ApiClient) => ({
    getInfrastructureProviders: {
        send: (mapIdentity) => apiClient.resources.infrastructureProvider.list({
            map_identity: mapIdentity,
        }),
        success: (response) => ({ infrastructureProviders: response.results }),
    },
});

export default connectAPI(apiCallback)(withRouter(TransformPoint));
